import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Image as ImageIcon,
  Collections,
  Wallpaper,
  CloudUpload,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { rewriteToCdn } from '../../utils/cdn';
import MPhotoSectionTabs from '../shared/MPhotoSectionTabs';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import SlotAspectCropDialog from '../shared/SlotAspectCropDialog';

type AssetTab = 'frames' | 'backgrounds';
type AssetsChannel = 'pro' | 'lite';

const PINK = '#e86a8a';
const PINK_SOFT = '#ff8ba7';

interface UserCustomFrameAssetsProps {
  channel?: AssetsChannel;
}

const UserCustomFrameAssets: React.FC<UserCustomFrameAssetsProps> = ({ channel = 'pro' }) => {
  const { userId, customFrameId } = useParams<{ userId: string; customFrameId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState<AssetTab>('frames');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    user: { _id: string; username: string; email: string };
    customFrame: api.CustomFrame;
  } | null>(null);
  const [userFrames, setUserFrames] = useState<api.AdminUserCustomFrameAsset[]>([]);
  const [backgrounds, setBackgrounds] = useState<api.AdminUserCustomFrameAsset[]>([]);
  const [hasLoadedFrames, setHasLoadedFrames] = useState(false);
  const [hasLoadedBackgrounds, setHasLoadedBackgrounds] = useState(false);
  const [bgCropOpen, setBgCropOpen] = useState(false);
  const [bgCropFile, setBgCropFile] = useState<File | null>(null);

  const loadAssets = useCallback(
    async (tab: AssetTab) => {
      if (!userId || !customFrameId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.getAdminUserCustomFrameAssets(
          userId,
          customFrameId,
          tab === 'frames' ? 'user_frame' : 'background',
          1,
          200,
          channel
        );

        const normalizedItems = (response.items || []).map((item) => ({
          ...item,
          frameImage: item.frameImage
            ? {
                ...item.frameImage,
                url: rewriteToCdn(item.frameImage.url),
              }
            : item.frameImage,
        }));

        setMeta({
          user: response.user,
          customFrame: response.customFrame,
        });

        if (tab === 'frames') {
          setUserFrames(normalizedItems);
          setHasLoadedFrames(true);
        } else {
          setBackgrounds(normalizedItems);
          setHasLoadedBackgrounds(true);
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Không thể tải danh sách tài nguyên cho custom frame';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [customFrameId, userId, channel]
  );

  useEffect(() => {
    setUserFrames([]);
    setBackgrounds([]);
    setMeta(null);
    setError(null);
    setActiveTab('frames');
    setHasLoadedFrames(false);
    setHasLoadedBackgrounds(false);
    loadAssets('frames');
  }, [customFrameId, userId, channel, loadAssets]);

  useEffect(() => {
    if (activeTab === 'backgrounds' && !hasLoadedBackgrounds) {
      loadAssets('backgrounds');
    }
  }, [activeTab, hasLoadedBackgrounds, loadAssets]);

  const firstSlotSize = useMemo(() => {
    const slot = meta?.customFrame?.photoPositions?.[0];
    const w = Number(slot?.width) || Number(meta?.customFrame?.width) || 1080;
    const h = Number(slot?.height) || Number(meta?.customFrame?.height) || 1440;
    return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
  }, [meta?.customFrame]);

  const uploadCroppedBackground = useCallback(
    async (croppedFile: File) => {
      if (!userId || !customFrameId) return;
      try {
        setUploading(true);
        const res = await api.uploadAdminUserCustomFrameAssets(
          userId,
          customFrameId,
          'background',
          [croppedFile],
          channel
        );
        toast.success(res.message || 'Upload nền thành công');
        setBgCropOpen(false);
        setBgCropFile(null);
        await loadAssets('backgrounds');
        setHasLoadedFrames(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Upload thất bại');
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [userId, customFrameId, channel, loadAssets]
  );

  const onDropUpload = useCallback(
    async (acceptedFiles: File[]) => {
      if (!userId || !customFrameId || acceptedFiles.length === 0) return;

      if (activeTab === 'backgrounds') {
        const file = acceptedFiles[0];
        if (!file) return;
        if (!meta?.customFrame?.photoPositions?.[0]) {
          toast.error('Khung chưa có ô slot — không thể căn tỉ lệ nền');
          return;
        }
        setBgCropFile(file);
        setBgCropOpen(true);
        return;
      }

      try {
        setUploading(true);
        const res = await api.uploadAdminUserCustomFrameAssets(
          userId,
          customFrameId,
          'user_frame',
          acceptedFiles.slice(0, 10),
          channel
        );
        toast.success(res.message || 'Upload thành công');
        await loadAssets('frames');
        setHasLoadedBackgrounds(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Upload thất bại');
      } finally {
        setUploading(false);
      }
    },
    [userId, customFrameId, activeTab, channel, loadAssets, meta?.customFrame?.photoPositions]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropUpload,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    multiple: activeTab !== 'backgrounds',
    maxFiles: activeTab === 'backgrounds' ? 1 : 10,
    disabled: uploading || loading,
  });

  const handleDelete = async (assetId: string) => {
    if (!userId || !customFrameId) return;
    try {
      await api.deleteAdminUserCustomFrameAsset(
        userId,
        customFrameId,
        assetId,
        activeTab === 'frames' ? 'user_frame' : 'background',
        channel
      );
      toast.success('Đã xóa');
      if (activeTab === 'frames') {
        setUserFrames((prev) => prev.filter((x) => x._id !== assetId));
      } else {
        setBackgrounds((prev) => prev.filter((x) => x._id !== assetId));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Xóa thất bại');
    }
  };

  const currentItems = useMemo(
    () => (activeTab === 'frames' ? userFrames : backgrounds),
    [activeTab, backgrounds, userFrames]
  );

  const chipSx = {
    height: 24,
    fontWeight: 700,
    fontSize: '0.7rem',
    borderRadius: 999,
  };

  if (!userId || !customFrameId) {
    return (
      <UserPageShell>
        <Alert severity="error">Thiếu thông tin user hoặc custom frame.</Alert>
      </UserPageShell>
    );
  }

  return (
    <UserPageShell enter>
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Typography
          component="h1"
          fontWeight={800}
          noWrap
          title={meta?.customFrame?.name}
          sx={{
            fontSize: { xs: '1.2rem', sm: '1.65rem' },
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
            color: '#2d2430',
          }}
        >
          {meta?.customFrame?.name || 'Custom Frame'}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 0.4, fontSize: { xs: '0.78rem', sm: '0.9rem' }, fontWeight: 600 }}
        >
          {meta?.user?.username || '…'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          {meta?.customFrame?.size && (
            <Chip
              size="small"
              label={meta.customFrame.size}
              sx={{
                ...chipSx,
                bgcolor: alpha(PINK, 0.14),
                color: PINK,
                border: 'none',
              }}
            />
          )}
          <Chip
            size="small"
            label={`${userFrames.length} frame`}
            sx={{
              ...chipSx,
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: alpha(PINK_SOFT, 0.45),
              color: PINK,
            }}
          />
          <Chip
            size="small"
            label={`${backgrounds.length} nền`}
            sx={{
              ...chipSx,
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: alpha(PINK_SOFT, 0.45),
              color: alpha('#2d2430', 0.65),
            }}
          />
        </Box>
      </Box>

      {meta?.customFrame && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(PINK_SOFT, 0.28),
            bgcolor: alpha(PINK_SOFT, 0.08),
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CustomFrameLayoutPreview
            width={meta.customFrame.width}
            height={meta.customFrame.height}
            size={meta.customFrame.size}
            photoPositions={meta.customFrame.photoPositions}
            maxHeight={{ xs: 200, sm: 260 }}
          />
        </Paper>
      )}

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <MPhotoSectionTabs
        value={activeTab}
        onChange={setActiveTab}
        accentColor={PINK}
        size="sm"
        sticky
        stickyTop={{ xs: 56, sm: 64 }}
        options={[
          {
            value: 'frames',
            label: isMobile ? `Frame (${userFrames.length})` : `User Frames (${userFrames.length})`,
            icon: <Collections fontSize="inherit" />,
          },
          {
            value: 'backgrounds',
            label: isMobile ? `Nền (${backgrounds.length})` : `Backgrounds (${backgrounds.length})`,
            icon: <Wallpaper fontSize="inherit" />,
          },
        ]}
      />

      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          mb: 2,
          mt: 1.5,
          px: 2,
          py: 2,
          borderRadius: 2.5,
          border: '1.5px solid',
          borderColor: isDragActive ? alpha(PINK, 0.55) : alpha(PINK_SOFT, 0.35),
          bgcolor: isDragActive ? alpha(PINK_SOFT, 0.22) : alpha(PINK_SOFT, 0.1),
          cursor: uploading ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ color: PINK }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography fontWeight={800} sx={{ color: PINK, fontSize: '0.9rem' }}>
            {uploading
              ? 'Đang upload...'
              : activeTab === 'frames'
                ? 'Thêm user frames cho user'
                : 'Thêm background (1 ảnh)'}
          </Typography>
          {activeTab === 'backgrounds' && (
            <Typography variant="caption" sx={{ color: PINK, fontWeight: 800, display: 'block' }}>
              Kích thước khuyến nghị: {firstSlotSize.width}×{firstSlotSize.height} px
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {activeTab === 'backgrounds'
              ? 'Chọn 1 ảnh → căn chỉnh · mỗi file ≤ 5MB'
              : 'Kéo thả hoặc click · tối đa 10 ảnh · mỗi file ≤ 5MB'}
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PINK }} />
        </Box>
      ) : currentItems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.05rem' } }}>
            {activeTab === 'frames'
              ? 'Chưa có user frame cho khung này'
              : 'Chưa có background cho khung này'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2 }}>
          {currentItems.map((item, index) => (
            <Grid item xs={6} sm={6} md={4} key={item._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(PINK_SOFT, 0.28),
                  overflow: 'hidden',
                  boxShadow: `0 4px 14px ${alpha(PINK, 0.08)}`,
                  position: 'relative',
                  '&:hover .delete-asset-btn': { opacity: 1 },
                  ...userCardEnterSx(index),
                }}
              >
                <IconButton
                  className="delete-asset-btn"
                  size="small"
                  onClick={() => handleDelete(item._id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: 'rgba(0,0,0,0.45)',
                    color: '#fff',
                    opacity: { xs: 1, sm: 0 },
                    transition: 'opacity 0.2s',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <Box
                  sx={{
                    bgcolor: alpha(PINK_SOFT, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: 132, sm: 180 },
                    p: 1,
                  }}
                >
                  {item.frameImage?.url ? (
                    <CardMedia
                      component="img"
                      image={item.frameImage.url}
                      alt=""
                      sx={{
                        maxHeight: { xs: 120, sm: 168 },
                        width: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 40, color: alpha(PINK, 0.35) }} />
                  )}
                </Box>
                <CardContent sx={{ py: { xs: 1, sm: 1.25 }, px: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                  <Typography
                    fontWeight={800}
                    noWrap
                    title={item.name}
                    sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' } }}
                  >
                    {item.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SlotAspectCropDialog
        open={bgCropOpen}
        sourceFile={bgCropFile}
        slotWidth={firstSlotSize.width}
        slotHeight={firstSlotSize.height}
        title="Căn chỉnh ảnh nền theo ô slot"
        hint="Kéo để dịch ảnh, zoom để phóng to. Ảnh sẽ được cắt đúng tỉ lệ ô slot đầu của khung trước khi upload."
        confirmLabel="Cắt & upload"
        onClose={() => {
          if (uploading) return;
          setBgCropOpen(false);
          setBgCropFile(null);
        }}
        onConfirm={uploadCroppedBackground}
      />
    </UserPageShell>
  );
};

export default UserCustomFrameAssets;
